import { extractPageParams } from "@jsonapi-serde/client";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Alert, Divider, Stack } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { z } from "zod/mini";
import IconButtonLink from "@/components/Link/IconButtonLink.js";
import { useQueryOptionsFactory } from "@/queries/index.js";
import ReviewCard from "@/routes/manage/reviews/-components/ReviewCard.js";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { pageParams } = Route.useSearch();
    const page = useSuspenseQuery(qof.review.list({ pageParams })).data;

    const pagination = extractPageParams(page.links ?? {});

    if (page.data.length === 0) {
        return <Alert severity="info">No reviews submitted yet.</Alert>;
    }

    return (
        <>
            <Stack spacing={2}>
                {page.data.map((review) => (
                    <ReviewCard review={review} key={review.id} />
                ))}
            </Stack>

            {(pagination.first || pagination.next) && (
                <>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                    <IconButtonLink
                        to="."
                        search={{ pageParams: pagination.first }}
                        disabled={!pagination.first}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <FirstPageIcon />
                    </IconButtonLink>

                    <IconButtonLink
                        to="."
                        search={{ pageParams: pagination.prev }}
                        disabled={!pagination.prev}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <KeyboardArrowLeftIcon />
                    </IconButtonLink>
                    <IconButtonLink
                        to="."
                        search={{ pageParams: pagination.next }}
                        disabled={!pagination.next}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <KeyboardArrowRightIcon />
                    </IconButtonLink>
                </>
            )}
        </>
    );
};

const searchSchema = z.object({
    pageParams: z._default(z.catch(z.record(z.string(), z.string()), { size: "10" }), {
        size: "10",
    }),
});

export const Route = createFileRoute("/manage/reviews/")({
    component: Root,
    validateSearch: searchSchema,
    loaderDeps: ({ search: { pageParams } }) => ({ pageParams }),
    loader: async ({ context, deps }) => {
        await context.queryClient.ensureQueryData(
            context.qof.review.list({
                pageParams: deps.pageParams,
            }),
        );
    },
});
